import { Table, Thead, Tbody, Tr, Th, Td } from '@components/core-ui/Table/Table';
import { TagMutedRounded } from '@components/core-ui/Tag/variations/TagMutedRounded';
import { routes } from '@routes';
import { Link } from 'react-router-dom';
import type { ProcessingPipelineJob } from '../types/dashboardTypes';
import { formatDateTime, formatSeconds } from '../utils';
import { SectionPagination } from './SectionPagination';

type TProps = {
  processingJobs: ProcessingPipelineJob[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  confirmMarkAttemptFailed: (attemptId: string) => void;
};

export const ProcessingJobsCard = ({
  processingJobs,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  confirmMarkAttemptFailed,
}: TProps) => (
  <div className="bg-[var(--color-surface)] shadow-[0_8px_10px_var(--color-shadow)] rounded-[10px] p-5 mb-5 w-full">
    <div className="flex flex-wrap gap-2 items-center justify-between mb-4">
      <div className="text-[var(--color-text-muted)] font-medium text-lg leading-[150%]">Longest processing jobs</div>
      <div className="text-xs text-[var(--color-text-muted)]">
        Ordered by time processing • {totalCount} job{totalCount === 1 ? '' : 's'}
      </div>
    </div>
    {processingJobs.length === 0 ? (
      <div className="p-2.5">No long-running processing jobs currently.</div>
    ) : (
      <>
        <div className="overflow-x-auto">
          <Table className="w-full text-sm text-left text-[var(--color-text)]">
            <Thead className="text-xs uppercase text-[var(--color-text-muted)]">
              <Tr className="border-b border-border">
                <Th className="py-2 pr-2 whitespace-nowrap">Job</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Status</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Time processing</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Since</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Latest attempt</Th>
                <Th className="py-2 pr-2 whitespace-nowrap text-right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {processingJobs.map((job: ProcessingPipelineJob) => (
                <Tr key={job.jobId} className="border-b border-border last:border-0">
                  <Td className="py-3 pr-2 align-top">
                    {job.jobId ? (
                      <Link
                        to={routes.adminJobDetails.make(job.jobId)}
                        className="text-[var(--color-text)] font-medium hover:underline"
                      >
                        {job.jobName}
                      </Link>
                    ) : (
                      <div className="text-[var(--color-text)] font-medium">{job.jobName}</div>
                    )}
                    <div className="text-[var(--color-text-muted)] text-xs flex flex-wrap gap-1">
                      <span>Job ID:</span>
                      {job.jobId ? (
                        <Link
                          to={routes.adminJobDetails.make(job.jobId)}
                          className="text-[var(--color-link)] hover:underline"
                        >
                          {job.jobId}
                        </Link>
                      ) : (
                        <span>-</span>
                      )}
                      <span>• Workspace: {job.workspaceId}</span>
                    </div>
                  </Td>
                  <Td className="py-3 pr-2 align-top">
                    <div className="inline-flex px-2 py-1 rounded-full bg-[var(--color-title)] text-[var(--color-on-surface)] text-[11px] uppercase whitespace-nowrap">
                      {job.status}
                    </div>
                  </Td>
                  <Td className="py-3 pr-2 align-top text-[var(--color-text)]">
                    {formatSeconds(job.processingDurationSeconds)}
                  </Td>
                  <Td className="py-3 pr-2 align-top text-[var(--color-text-muted)] text-xs">
                    {formatDateTime(job.statusChangeDate)}
                  </Td>
                  <Td className="py-3 pr-2 align-top text-[var(--color-text)]">
                    <div className="flex flex-wrap gap-1">
                      <div className="text-sm">{job.latestAttemptStatus ?? '-'}</div>
                      {job.latestAttemptAutoRetried ? (
                        <TagMutedRounded className="inline-flex text-[11px] font-semibold uppercase whitespace-nowrap !px-2 !py-1">
                          AUTO-RETRIED
                        </TagMutedRounded>
                      ) : null}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {formatDateTime(job.latestAttemptStartedAt)}
                    </div>
                  </Td>
                  <Td className="py-3 pr-2 align-top text-right">
                    <div className="flex gap-2 flex-wrap justify-end">
                      {job.latestAttemptId ? (
                        <button
                          type="button"
                          onClick={() => confirmMarkAttemptFailed(job.latestAttemptId!)}
                          className="text-xs px-3 py-2 rounded-md border border-orange-500 text-orange-800 bg-orange-50 hover:bg-orange-100"
                        >
                          Mark failed
                        </button>
                      ) : null}
                      {job.jobId ? (
                        <Link
                          to={routes.adminJobDetails.make(job.jobId)}
                          className="text-xs px-3 py-2 rounded-md border border-border text-[var(--color-text)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-muted)] whitespace-nowrap"
                        >
                          View job
                        </Link>
                      ) : null}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
        <div className="mt-4">
          <SectionPagination
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      </>
    )}
  </div>
);
